import React, { useEffect, useState } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../services/firebaseService";
// Both imported rather than redeclared. `FIREBASE_AUTH_IDB` was declared in
// both files, justified by a comment saying this file could not import it —
// which stopped being true the moment this import line existed.
import { raceWithBoundedTimeout, FIREBASE_AUTH_IDB } from "../services/authPersistence";
import {
  FIREBASE_APP_CHECK_IDB,
  FIREBASE_HEARTBEAT_IDB,
  FIREBASE_IDB_OPENABLE_PROBE,
} from "../services/indexedDbOpenBound";
import { getTitleForPath } from "../lib/route-titles";
import { revokeApexSession, type ApexRevokeOutcome } from "../lib/apexSessionRevoke";
import { suppressApexSsoBootstrap } from "../lib/apexSsoSuppression";

/**
 * Public /logout rescue route (KB-1670).
 *
 * Mounted OUTSIDE the AuthContext "Loading your session…" gate (see App.tsx),
 * so it recovers a session wedged on auth bootstrap — corrupt Firebase auth
 * persistence in IndexedDB, or a stuck auth/App-Check probe on localhost→prod.
 * On mount it UNCONDITIONALLY signs out, wipes web storage + IndexedDB, and
 * hard-redirects to /login. It must never depend on AuthContext being resolved.
 *
 * KB-3425 — the two original defects were both in the IndexedDB step; review
 * found four more on the rescue path, listed after them.
 *
 * 1. **It was unbounded.** `deleteDatabase` had handlers for `success`,
 *    `error` and `blocked` and no deadline, and the results were joined with
 *    `Promise.all`. KB-3386 measured `indexedDB.open()` on the wedged stores
 *    firing NONE of those three events in 8001 ms. Against a store in that
 *    state the promise never settled, `Promise.all` never settled, and
 *    `window.location.replace` — the step that actually performs the rescue —
 *    never ran.
 *
 * 2. **Its failure rendered as success.** What the user saw while that
 *    happened was a spinner over "Signing out…", indefinitely: a calm,
 *    plausible surface telling the reader to stop looking, on the one route a
 *    locked-out user is told to visit. Now a wipe that cannot finish says so,
 *    and offers the browser-executed path (`/reset-local-app-data`) that does
 *    not go through the IndexedDB API at all.
 *
 * Found in review, and every one of them is the same family — a rescue route
 * that cannot report its own failure:
 *
 * 3. An `onerror` delete was counted as finished, so the page redirected back
 *    into the store it had just failed to clear (round 1).
 * 4. An `indexedDB` that THROWS on access was then counted as a failed delete,
 *    which stranded users whose request was never made at all (round 2) — see
 *    `DeleteOutcome` below for why those are different.
 * 5. The stuck screen had no way onward, so its only control was one that can
 *    also fail: a dead end rendered as a rescue (round 2).
 * 6. The reset handler gated on `res.ok` and discarded the endpoint's body, so
 *    a 200 whose body said `ok: false` read as success (round 1).
 *
 * ⚠️ NOT claimed: that `deleteDatabase` on a corrupt store hangs. KB-3386
 * measured `open()` on the corrupt store, and `deleteDatabase` only against a
 * NON-EXISTENT name (1 ms — a control proving the subsystem responsive). The
 * delete-on-corrupt case is unmeasured. The bound is here because this code
 * was unbounded by construction and so could not report either way.
 *
 * ⚠️ And the origin matters. Every `open()`-never-settles measurement cited
 * here was taken on `kidsbooked.com`, NOT on `pro.kidsbooked.com`. On pro, what
 * is established (KB-3425 AC-1, 2026-09-11) is that clearing the origin's
 * stored data via Safari → Manage Website Data restored login. That is a
 * COMPOUND remedy: it removes IndexedDB, localStorage, cookies and CacheStorage
 * together, so it proves stored browser state caused the lockout and does NOT
 * prove which artifact did. The IndexedDB-specific measurement was never taken
 * on this origin and can no longer be taken, because clearing the data
 * destroyed the evidence. Treat the IndexedDB mechanism as strongly supported
 * by analogy and by the compound remedy working — never as measured on pro.
 */

const REDIRECT_TARGET = "/login";
// Cap the wait on a hung/rejecting signOut so it can never block the rescue.
const SIGN_OUT_TIMEOUT_MS = 1500;
// KB-3425: cap the wait on EACH deleteDatabase, and on the enumeration. Sized
// well under the ~8 s at which a wedged store is already known to be silent —
// there is nothing to learn by waiting longer, and every second here is spent
// on a screen that cannot tell the user anything yet.
const IDB_DELETE_TIMEOUT_MS = 2000;
// The browser-executed escape hatch (KB-3425 AC-3). POST-only and
// intent-gated on purpose — see functions/src/auth/resetLocalAppData.ts.
const RESET_ENDPOINT = "/reset-local-app-data";
const RESET_INTENT_HEADER = "x-kb-reset-intent";
// KB-3425: the last-resort button must also be able to give up. Longer than the
// IndexedDB bounds because this one crosses a network and a cold Cloud Function
// start, and short enough that a user is not left watching it.
const RESET_FETCH_TIMEOUT_MS = 10000;

/**
 * Deleted by name on every rescue, not only when enumeration finds them. Auth's
 * old persistence store, plus the three whose opens auth requests wait on
 * (KB-3467): the heartbeat door opens the openable-probe store before its own,
 * so a wedge on either blocks it.
 */
const KNOWN_FIREBASE_STORES = [
  FIREBASE_AUTH_IDB,
  FIREBASE_HEARTBEAT_IDB,
  FIREBASE_APP_CHECK_IDB,
  FIREBASE_IDB_OPENABLE_PROBE,
];
const ENUMERATION_TIMED_OUT = Symbol("logout/databases-timed-out");

/**
 * ⚠️ `unavailable` is a distinct outcome from `error`, and the distinction is
 * load-bearing (round-2 finding 5).
 *
 * `error` means we asked the store to delete itself and it said no — the data
 * is still there, so the wipe did NOT finish. `unavailable` means the
 * `indexedDB` API threw on access, so the request was never made: locked-down
 * profiles, some embedded WebViews, private-mode engines. There is nothing
 * there to clear and nothing failed, so blocking the rescue on it would strand
 * a user on a failure that did not happen.
 *
 * Collapsing the two into `error` is what turned round 1's correct fix ("an
 * errored delete is not a finished delete") into a permanent dead end on that
 * class of browser.
 */
type DeleteOutcome = "deleted" | "blocked" | "error" | "timed-out" | "unavailable";

function deleteDatabase(name: string, boundMs: number): Promise<DeleteOutcome> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (outcome: DeleteOutcome) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(outcome);
    };
    // Armed BEFORE the request, so a call that throws or never fires an event
    // is still bounded. This is the whole fix: the three handlers below cover
    // a store that answers, and a wedged store answers with nothing at all.
    const timer = setTimeout(() => finish("timed-out"), boundMs);

    try {
      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = () => finish("deleted");
      request.onerror = () => finish("error");
      // A live connection blocking the delete is not a failure of the rescue:
      // the hard reload below drops those connections anyway.
      request.onblocked = () => finish("blocked");
    } catch (err) {
      console.warn(`[logout] deleteDatabase(${name}) is unavailable`, err);
      finish("unavailable");
    }
  });
}

/**
 * Returns the names whose store was NOT cleared. An empty array means every
 * store we know about reported success — it does NOT mean the profile is
 * clean, only that nothing we asked about failed.
 */
async function clearIndexedDb(): Promise<string[]> {
  const names = new Set<string>(KNOWN_FIREBASE_STORES);
  try {
    // Bounded too: `databases()` is the same subsystem, and enumerating is not
    // worth hanging the rescue over.
    //
    // Routed through the shared helper rather than a hand-rolled
    // `Promise.race`: the helper's stated contract is that the timer is ALWAYS
    // cleared (KB-2500), and the hand-rolled version here left a 2 s timer
    // armed after `databases()` won.
    //
    // KB-3467: the expiry value is a private sentinel, not `[]`. `[]` is also
    // what a profile with no stores answers, so racing `[]` made "could not
    // enumerate" and "enumerated nothing" the same value. What is deleted does
    // not depend on the difference any more — every store known to wedge a
    // login is deleted by name either way — but the record of what happened
    // does.
    if (typeof indexedDB.databases !== "function") {
      console.warn("[logout] indexedDB.databases() is not supported here; deleting the known Firebase stores by name only");
    } else {
      const listed = await raceWithBoundedTimeout<IDBDatabaseInfo[] | typeof ENUMERATION_TIMED_OUT>(
        indexedDB.databases(),
        ENUMERATION_TIMED_OUT,
        IDB_DELETE_TIMEOUT_MS,
      );
      if (listed === ENUMERATION_TIMED_OUT) {
        console.warn(
          `[logout] indexedDB.databases() did not answer within ${IDB_DELETE_TIMEOUT_MS} ms; deleting the known Firebase stores by name only`,
        );
      } else {
        for (const info of listed) {
          if (info?.name) names.add(info.name);
        }
      }
    }
  } catch (err) {
    console.warn("[logout] indexedDB.databases() failed", err);
  }

  const ordered = [...names];
  const outcomes = await Promise.all(
    ordered.map((name) => deleteDatabase(name, IDB_DELETE_TIMEOUT_MS)),
  );
  // `Promise.all` is safe here ONLY because every member is now individually
  // bounded — that is what changed. Unbounded members were the original bug.
  //
  // ⛔ `error` counts as unfinished, and treating it as finished was a defect
  // of exactly the shape this route is being fixed for. A `deleteDatabase` that
  // fires `onerror` did NOT delete the store; classifying it as done sent the
  // user through `window.location.replace("/login")` straight back into the
  // store the wipe had just failed to clear, silently, with the rescue screen
  // reporting nothing. `blocked` is different and is argued for at the handler:
  // a live connection holding the delete is cleared by the hard reload anyway.
  return ordered.filter((_, i) => outcomes[i] === "timed-out" || outcomes[i] === "error");
}

const LogoutPage: React.FC = () => {
  // KB-2619: the one App.tsx route rendered OUTSIDE AuthLayout, so it's the
  // one route AnalyticsRouteTracker never sees — set the title directly via
  // the shared map rather than hand-rolling a string here.
  useEffect(() => {
    document.title = getTitleForPath('/logout');
  }, []);

  const [stuck, setStuck] = useState<string[] | null>(null);
  const [resetState, setResetState] = useState<"idle" | "sending" | "failed">("idle");

  useEffect(() => {
    const rescue = async () => {
      // 1. Best-effort Firebase sign-out. Never let a hung/rejected signOut
      //    block the wipe — recovering a wedged session is the whole point.
      //
      //    KB-3994: the apex (kidsbooked.com) session is revoked alongside it,
      //    as the dropdown's federated sign-out does. Without that, the /login
      //    this page reloads into adopts the still-live apex cookie straight
      //    back into pro. Concurrent, and under the same bound, so the step
      //    takes no longer than it did. `revokeApexSession` never throws and
      //    carries its own abort; a revoke still in flight at the bound keeps
      //    running through the steps below.
      const revoke = raceWithBoundedTimeout<ApexRevokeOutcome | "timed-out">(
        revokeApexSession(),
        "timed-out",
        SIGN_OUT_TIMEOUT_MS,
      );
      try {
        // Through the shared helper, not a hand-rolled race. The hand-rolled
        // one here leaked its timer — and this file had already replaced the
        // identical pattern 60 lines down while leaving this one, so it
        // contradicted its own comment about why the helper exists.
        await raceWithBoundedTimeout<void>(signOut(auth), undefined, SIGN_OUT_TIMEOUT_MS);
      } catch (err) {
        console.warn("[logout] signOut failed (continuing rescue)", err);
      }
      const revokeOutcome = await revoke;
      if (revokeOutcome !== "revoked") {
        console.warn(`[logout] apex session revoke: ${revokeOutcome} (continuing rescue)`);
      }

      // 2. Clear web storage.
      try {
        localStorage.clear();
      } catch (err) {
        console.warn("[logout] localStorage.clear() failed", err);
      }
      try {
        sessionStorage.clear();
      } catch (err) {
        console.warn("[logout] sessionStorage.clear() failed", err);
      }
      // KB-3994: AFTER the clear, which would otherwise erase it. Tells the
      // /login load below not to adopt the apex session, including when the
      // revoke above did not land. Written before the IndexedDB step, so the
      // stuck screen's "Continue to sign-in anyway" gets it too.
      suppressApexSsoBootstrap();

      // 3. Delete IndexedDB (Firebase auth persistence + any app DBs).
      const unfinished = await clearIndexedDb();

      if (unfinished.length > 0) {
        // 4a. The wipe could not finish. Do NOT redirect — a reload into the
        //     same wedged store lands the user back where they started, and
        //     doing it silently would be the "calm wrong answer" this route's
        //     own failure mode already was. Say what happened and offer the
        //     path that does not use this API.
        console.warn("[logout] IndexedDB delete did not complete for", unfinished);
        setStuck(unfinished);
        return;
      }

      // 4b. Hard redirect — a full navigation re-bootstraps AuthContext from a
      //     clean slate. Deliberately not react-router navigate(): we want a
      //     real reload, and this route intentionally has no router dependency.
      window.location.replace(REDIRECT_TARGET);
    };

    void rescue();
  }, []);

  const runBrowserReset = async () => {
    setResetState("sending");
    // Bounded, because this is the LAST control a locked-out user has. An
    // unbounded fetch leaves the button disabled reading "Asking your
    // browser…" forever — the same never-settling-spinner shape this route is
    // being fixed for, reproduced on the fallback. `AbortController` rather
    // than a bare race: a race would leave the request running and the browser
    // could still act on a late `Clear-Site-Data` after we had told the user it
    // failed.
    const abort = new AbortController();
    const bail = setTimeout(() => abort.abort(), RESET_FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(RESET_ENDPOINT, {
        method: "POST",
        headers: { [RESET_INTENT_HEADER]: "1" },
        cache: "no-store",
        signal: abort.signal,
      });
      // Round-1 finding 9: `res.ok` alone is not the endpoint's answer. It
      // returns a JSON body — `{ ok, sent, detail }` — and a 200 whose body
      // says `ok: false` would have been read as success by an `res.ok` test,
      // which is the same not-reading-the-answer shape as everything else on
      // this route. A body we cannot parse is treated as a failure rather than
      // assumed good, for the same reason.
      let body: { ok?: boolean; sent?: string } | null = null;
      try {
        body = (await res.json()) as { ok?: boolean; sent?: string };
      } catch {
        body = null;
      }
      if (!res.ok || body?.ok !== true) {
        console.warn("[logout] reset endpoint refused", { status: res.status, body });
        setResetState("failed");
        return;
      }
      // The browser acts on the response header, not on anything we do here —
      // so this log is the only record of what was asked for, and it states
      // what was SENT, never that anything was cleared (AC-5).
      console.info("[logout] Clear-Site-Data requested", body.sent ?? "(directives not reported)");
      window.location.replace(REDIRECT_TARGET);
    } catch (err) {
      // Includes the AbortError from the deadline above. Same rendering either
      // way: we do not know whether the browser acted, and we say so rather
      // than leaving the button spinning.
      console.warn("[logout] reset endpoint failed", err);
      setResetState("failed");
    } finally {
      clearTimeout(bail);
    }
  };

  if (stuck) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold text-gray-900 mb-2">
            Couldn’t finish clearing your data
          </h1>
          {/* AC-5: state what was observed, never a diagnosis. We know the
              delete did not answer within the deadline. We do NOT know why,
              and "something is holding your data" was already wrong once. */}
          <p className="text-sm text-gray-600 mb-4">
            Your browser didn’t respond when we tried to clear stored data for this
            site{formatStoreNames(stuck)}. We don’t know why — this step is handled by the
            browser, not by the app.
          </p>
          <p className="text-sm text-gray-600 mb-4">
            You can ask the browser itself to clear it, which uses a different
            mechanism than the one that just stalled. This also signs you out of
            kidsbooked.com, not just this site.
          </p>
          <button
            type="button"
            onClick={runBrowserReset}
            disabled={resetState === "sending"}
            className="px-4 py-2 rounded bg-kidsbooked-blue text-white text-sm disabled:opacity-60"
          >
            {resetState === "sending" ? "Asking your browser…" : "Clear site data"}
          </button>
          {/* ⛔ A rescue screen must ALWAYS offer a way onward (round-2 finding
              5, dispatcher ruling). Without this the page is a terminal state
              whose only control is one that may also fail — which is a dead end
              rendered as a rescue. Deliberately a plain link and a full
              navigation, matching the rest of this route's no-router-dependency
              rule.

              It claims nothing about the steps above. Sign-out and the two
              web-storage clears each swallow their own failure and carry on, so
              reaching this screen says only that the IndexedDB step did not
              finish — not that the others worked. */}
          <p className="mt-6 text-sm text-gray-600">
            <a href={REDIRECT_TARGET} className="underline">
              Continue to sign-in anyway
            </a>
          </p>
          {resetState === "failed" && (
            <p className="mt-4 text-sm text-gray-600">
              That didn’t work either. As a last resort, clear this site’s data from
              your browser’s settings — in Safari, Settings → Privacy → Manage
              Website Data.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
        <p className="text-gray-600">Signing out…</p>
      </div>
    </div>
  );
};

/** Names the stores only when we actually have names worth showing. */
function formatStoreNames(names: string[]): string {
  if (names.length === 0) return "";
  return ` (${names.join(", ")})`;
}

export default LogoutPage;
